import { ISanityVendorConfigs } from '@rbi-ctg/menu';
import {
  IInRestaurantIncentiveInput,
  IInRestaurantOrderLineItem,
  IVendorConfigsInput,
} from 'generated/graphql-gateway';
import { isCombo, isItem } from 'utils/menu/is-menu-type';
import { blockContentToPlainText } from 'utils/sanity';

import {
  IInRestaurantRedemptionCart,
  IInRestaurantRedemptionCartEntry,
  IInRestaurantRedemptionEntryType,
  IInRestaurantRedemptionOfferEntry,
  IOfferRedemption,
  IRewardRedemption,
  isRewardCartEntry,
} from './hooks/use-in-restaurant-redemption-cart';

// the accepted rbi gateway vendors
const lineItemVendors = [
  'carrols',
  'carrolsDelivery',
  'hdx',
  'ncr',
  'ncrDelivery',
  'productNumber',
  'productNumberDelivery',
  'qdi',
  'qdiDelivery',
  'qst',
  'rpos',
  'rposDelivery',
  'sicom',
  'sicomDelivery',
  'tablet',
  'tabletDelivery',
];

// filter invalid vendors
export const filterVendorConfigs = (vendorConfig: ISanityVendorConfigs) =>
  Object.keys(vendorConfig).reduce((accum, vendorKey) => {
    if (lineItemVendors.indexOf(vendorKey) !== -1) {
      accum[vendorKey] = vendorConfig[vendorKey];
    }

    return accum;
  }, {});

const getIncentiveVendorConfigs = (
  cartEntry: IInRestaurantRedemptionCartEntry
): { incentiveVendorConfigs: Array<IVendorConfigsInput> } | null => {
  if (cartEntry.type !== IInRestaurantRedemptionEntryType.REWARD) {
    return null;
  }

  const { reward } = cartEntry.details as IRewardRedemption;
  const incentiveVendorConfigs = reward.incentives?.reduce<Array<IVendorConfigsInput>>(
    (acc, incentive) => {
      if (incentive) {
        if (isItem(incentive) && incentive?.vendorConfigs) {
          acc.push(filterVendorConfigs(incentive.vendorConfigs));
        } else if (isCombo(incentive) && incentive?.mainItem?.vendorConfigs) {
          acc.push(filterVendorConfigs(incentive.mainItem.vendorConfigs));
        }
      }

      return acc;
    },
    []
  );

  return incentiveVendorConfigs && incentiveVendorConfigs.length
    ? { incentiveVendorConfigs }
    : null;
};

const getOfferDetail = (
  cartEntry: IInRestaurantRedemptionCartEntry
): IOfferRedemption['offer'] | null => {
  if (isRewardCartEntry(cartEntry)) {
    return null;
  }

  const {
    details: { offer },
  } = cartEntry as IInRestaurantRedemptionOfferEntry;

  return offer;
};

const getIncentivePluFromCartEntry = (
  cartEntry: IInRestaurantRedemptionCartEntry,
  offerType: IInRestaurantRedemptionEntryType.OFFER | IInRestaurantRedemptionEntryType.LEGACY_OFFER
): string | undefined | null => {
  if (cartEntry.type !== offerType) {
    return null;
  }

  const { shortCode } = getOfferDetail(cartEntry) || {};

  return shortCode;
};

const getNameFromCartEntry = (
  cartEntry: IInRestaurantRedemptionCartEntry
): Pick<IInRestaurantIncentiveInput, 'name'> | null => {
  const offer = getOfferDetail(cartEntry);
  if (!offer) {
    return null;
  }
  const stringOfferName = blockContentToPlainText(offer?.name?.localeRaw || []);

  return { name: stringOfferName };
};

export const generateOrderLineItems = (
  cart: IInRestaurantRedemptionCart
): Array<IInRestaurantOrderLineItem> =>
  cart.map((cartEntry: IInRestaurantRedemptionCartEntry) => {
    const { quantity, referenceId, incentiveId, type, vendorConfigs } = cartEntry;
    const offerCode = getIncentivePluFromCartEntry(
      cartEntry,
      IInRestaurantRedemptionEntryType.OFFER
    );
    const incentivePlu = getIncentivePluFromCartEntry(
      cartEntry,
      IInRestaurantRedemptionEntryType.LEGACY_OFFER
    );

    return {
      incentive: {
        id: incentiveId,
        ...getNameFromCartEntry(cartEntry),
        ...getIncentiveVendorConfigs(cartEntry),
        ...(offerCode && { offerCode }),
        type,
      },
      ...(incentivePlu && { incentivePlu }),
      quantity,
      referenceId,
      vendorConfigs: filterVendorConfigs(vendorConfigs),
    };
  });
